import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { IDictionaryValue } from 'APIPackage/DictionariesModule/interfaces/IDictionaryValue';
import { IKnoOrganizationDictionary } from 'APIPackage/DictionariesModule/interfaces/IKnoOrganizationDictionary';
import { ISupervisionType } from 'APIPackage/DictionariesModule/interfaces/ISupervisionType';
import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';
import { IKnmReason248 } from 'APIPackage/KnmModule/interfaces/IKnmReason248';
import { IKnmEvent248 } from 'APIPackage/KnmModule/interfaces/IKnmEvent248';
import { IKnmIndex } from 'APIPackage/KnmModule/interfaces/IKnmIndex';
import { IKnmObject248 } from 'APIPackage/KnmModule/interfaces/IKnmObject248';
import { IKnmRequirementsNpa } from 'APIPackage/KnmModule/interfaces/IKnmRequirementsNpa';
import { IKnmRequirementsNpa248 } from 'APIPackage/KnmModule/interfaces/IKnmRequirementsNpa248';
import { IKnmSubject248 } from 'APIPackage/KnmModule/interfaces/IKnmSubject248';
import {
  fetchKnm,
  fetchKnmSubjectsWithImpossibilityActNotRepeatedInLinkedKnms,
} from 'APIPackage/KnmModule/services/knmApi';
import { fetchDictValue } from 'CorePackage/DictionariesModule/common/fetchDictValue';
import { addMonths, parseDate } from 'CorePackage/MainModule/common/formatDate';
import {
  hasKnmPrescription,
  isSourceCreatedByOldRules,
  LEGACY_NO_PREFILL,
  LinkValidationNoPrescriptionMessageText,
} from 'CorePackage/MainModule/common/prescriptionLegacy';
import { DATE_FORMAT_ISO_FNS, DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { ROUTE_KNM, ROUTE_PM } from 'CorePackage/MainModule/routes/routes';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { KnmReasonTypeNameEnum } from 'KnmPackage/MainModule/enums/KnmReasonTypeNameEnum';

import { getMatchedViolation } from '../../../functions/getMatchedViolation';
import {
  convertErknmOrganizationsFromLink,
  convertErknmRequirementsFromLink,
  convertEventsFromLink,
  convertObjectsFromLink,
  convertPmOrganizationsFromLink,
  convertPmRequirementsFromLink,
} from './convertLinkedData';
import {
  getLinkedPassportIdsMessageText,
  getLinkValidationMessageText,
  LinkValidationGeneralMessageText,
  LinkValidationPrescriptionMessageText,
  LinkValidationReasonImpossibleByControlledPersonActionsErrorMessage,
} from './linkValidationMessages';
import {
  getErknmPrescriptions,
  getPrescriptionKey,
  ILinkedPrescription,
  isPrescription,
  isPrescriptionNotCompleted,
  TPrescriptionDecision,
} from './prescriptions';

/** Данные связанного паспорта, которыми заполняется текущий паспорт */
export interface ILinkedPassportData {
  organizations: IKnmSubject248[] | null;
  objects: IKnmObject248[] | null;
  events: IKnmEvent248[] | null;
  requirements: IKnmRequirementsNpa248[] | IKnmRequirementsNpa[] | null;
  isPrescriptionFailed?: boolean;
}

/**
 * Строка - текст ошибки, объект - данные для копирования, undefined - проверять нечего.
 * LEGACY_NO_PREFILL - исходный паспорт создан до включения настройки [4.1.31]:
 * ссылка допустима, но разделы не предзаполняются.
 */
export type TLinkValidationResult = string | typeof LEGACY_NO_PREFILL | ILinkedPassportData | undefined;

interface IValidateLinkCommonParams {
  linkedKnm: IKnm;
  isEnablePrescriptionCheck: boolean;
  isPm: boolean;
  erknmStartDateString?: string;
}

interface IValidateImpossibilityOfPreviousLinkParams extends IValidateLinkCommonParams {
  settings: IAdminSetting[];
  isEnablePaperlessDocument: boolean;
  controllingOrganizationIds?: string;
  passportId?: number;
}

interface IValidatePrescriptionCheckLinkParams extends IValidateLinkCommonParams {
  settings: IAdminSetting[];
  controllingOrganizationIds?: string;
  kindControlCode?: string;
  selectedPrescriptionKeys?: string[];
}

export interface IValidateSelectedLinkKnmParams
  extends Omit<IValidateImpossibilityOfPreviousLinkParams, 'linkedKnm' | 'isEnablePrescriptionCheck'>,
    Omit<IValidatePrescriptionCheckLinkParams, 'linkedKnm' | 'isEnablePrescriptionCheck'> {
  knm: IKnmIndex;
  // Уже загруженный паспорт по ссылке, чтобы не запрашивать его повторно
  cachedLinkKnm?: IKnm;
  reasonTypeName?: string;
  isErp?: boolean;
}

const IMPOSSIBILITY_ACT_KNM_TYPE_IDS = [4, 5];
const IMPOSSIBILITY_ACT_TYPE_ID = 2;
const PM_IMPOSSIBILITY_ACT_TYPE_ID = 4;
const CANCELED_REASON_CONTROLLED_PERSON_ACTIONS_ID = 3;

const isPassportByCheckPrescription = (reasonTypeName?: string) =>
  reasonTypeName === KnmReasonTypeNameEnum.PRESCRIPTION_CHECK;

/**
 * Если связанный паспорт сам создан по проверке предписания, то текущий должен быть помечен как проблемный.
 * Исключение [4.1.31]: паспорт-прародитель создан до включения настройки — тогда не помечаем.
 */
const isPassportByCheckPrescriptionFailed = async ({
  linkedReason,
  isEnablePrescriptionCheck,
  settings,
}: {
  linkedReason?: IKnmReason248;
  isEnablePrescriptionCheck: boolean;
  settings: IAdminSetting[];
}): Promise<boolean> => {
  if (!isEnablePrescriptionCheck || !isPassportByCheckPrescription(linkedReason?.reason?.type?.name)) {
    return false;
  }

  const grandSourceId = linkedReason?.reason?.links?.[0]?.id;

  if (!grandSourceId) {
    return true;
  }

  const grandSource: IKnm = await fetchKnm(grandSourceId);

  return !isSourceCreatedByOldRules(grandSource.created, settings);
};

const validateImpossibilityOfPreviousLink = async ({
  linkedKnm,
  isEnablePrescriptionCheck,
  settings,
  isEnablePaperlessDocument,
  isPm,
  erknmStartDateString,
  controllingOrganizationIds,
  passportId,
}: IValidateImpossibilityOfPreviousLinkParams): Promise<TLinkValidationResult> => {
  const knmDataTypeId = linkedKnm.type?.id;
  let dictionaryValue: IDictionaryValue<IKnoOrganizationDictionary> | undefined;

  if (linkedKnm?.knmErknm?.knoOrganization?.dictId) {
    dictionaryValue = await fetchDictValue<IKnoOrganizationDictionary>(linkedKnm.knmErknm.knoOrganization);
  }
  if (dictionaryValue && dictionaryValue?.value.idStr?.toString() !== controllingOrganizationIds) {
    return LinkValidationGeneralMessageText;
  }

  if (knmDataTypeId && IMPOSSIBILITY_ACT_KNM_TYPE_IDS.includes(knmDataTypeId)) {
    let organizations = linkedKnm.knmErknm?.organizations?.filter(
      org => org?.act?.typeAct?.id === IMPOSSIBILITY_ACT_TYPE_ID,
    );
    const acts = organizations?.map(item => item.act);

    if (!acts || !acts.length) {
      return getLinkValidationMessageText();
    }

    const act = acts[0];

    if (!(act?.document?.attachments && act?.document?.attachments[0].dateCreate && erknmStartDateString)) {
      return LinkValidationGeneralMessageText;
    }

    const actDate = parseDate({
      date: act?.document?.attachments[0].dateCreate,
      parseFormat: DATE_FORMAT_ISO_FNS,
    });
    const erknmStartDate = parseDate({
      date: erknmStartDateString,
      parseFormat: DATE_FORMAT_LONG_FNS,
    });

    if (!actDate || !erknmStartDate) {
      return LinkValidationGeneralMessageText;
    }

    const actDatePlus3Month = addMonths(actDate, 3);

    actDatePlus3Month.setHours(0, 0, 0, 0);
    actDate.setHours(0, 0, 0, 0);

    if (erknmStartDate < actDate) {
      return LinkValidationGeneralMessageText;
    }

    if (actDatePlus3Month <= erknmStartDate) {
      return getLinkValidationMessageText();
    }

    const response = await fetchKnmSubjectsWithImpossibilityActNotRepeatedInLinkedKnms(linkedKnm.id, passportId);

    if (!response) {
      return LinkValidationGeneralMessageText;
    }

    organizations = response.subjects;

    if (!organizations || !organizations.length) {
      if (response.linkedPassportIds?.length > 0) {
        return getLinkedPassportIdsMessageText(response, passportId);
      }

      return LinkValidationGeneralMessageText;
    }

    if (linkedKnm.knmErknm?.objects) {
      const isPrescriptionFailed = await isPassportByCheckPrescriptionFailed({
        linkedReason: linkedKnm.knmErknm?.reasons?.[0],
        isEnablePrescriptionCheck,
        settings,
      });

      return {
        isPrescriptionFailed,
        organizations: convertErknmOrganizationsFromLink(organizations),
        objects: convertObjectsFromLink(linkedKnm.knmErknm?.objects),
        events: convertEventsFromLink(linkedKnm.knmErknm?.events),
        requirements: convertErknmRequirementsFromLink(linkedKnm.knmErknm?.requirementsNew),
      };
    }
  }

  if (linkedKnm.pmErknm && isEnablePaperlessDocument) {
    const act = linkedKnm.pmErknm?.subject?.act;
    const hasImpossibilityAct = act?.typeAct?.id === PM_IMPOSSIBILITY_ACT_TYPE_ID;

    const hasReasonImpossibleByControlledPersonActions = (() => {
      const id = Number(act?.canceledReason?.id);

      return Number.isFinite(id) && id === CANCELED_REASON_CONTROLLED_PERSON_ACTIONS_ID;
    })();

    if (!isPm && !hasReasonImpossibleByControlledPersonActions) {
      return LinkValidationReasonImpossibleByControlledPersonActionsErrorMessage;
    }

    if (linkedKnm?.pmErknm?.knoOrganization?.dictId) {
      dictionaryValue = await fetchDictValue<IKnoOrganizationDictionary>(linkedKnm.pmErknm.knoOrganization);
    }
    if (
      !(
        hasImpossibilityAct &&
        act?.document?.attachments &&
        act?.document?.attachments[0].dateCreate &&
        erknmStartDateString
      ) ||
      dictionaryValue?.value.idStr?.toString() !== controllingOrganizationIds
    ) {
      return LinkValidationGeneralMessageText;
    }

    const actDate = parseDate({
      date: act?.document?.attachments[0].dateCreate,
      parseFormat: DATE_FORMAT_ISO_FNS,
    });
    const erknmStartDate = parseDate({
      date: erknmStartDateString,
      parseFormat: DATE_FORMAT_LONG_FNS,
    });

    if (!actDate || !erknmStartDate) {
      return undefined;
    }

    const actDatePlus3Month = addMonths(actDate, 3);

    actDatePlus3Month.setHours(0, 0, 0, 0);
    actDate.setHours(0, 0, 0, 0);

    if (erknmStartDate < actDate) {
      return LinkValidationGeneralMessageText;
    }

    if (actDatePlus3Month <= erknmStartDate) {
      return getLinkValidationMessageText();
    }

    const organization = linkedKnm.pmErknm?.subject;

    const response = await fetchKnmSubjectsWithImpossibilityActNotRepeatedInLinkedKnms(linkedKnm.id, passportId);

    if (!response) {
      return LinkValidationGeneralMessageText;
    }

    if (response.linkedPassportIds?.length > 0) {
      return getLinkedPassportIdsMessageText(response, passportId);
    }

    if (organization && linkedKnm.pmErknm?.objects) {
      const isPrescriptionFailed = await isPassportByCheckPrescriptionFailed({
        linkedReason: linkedKnm.pmErknm?.reasons?.[0],
        isEnablePrescriptionCheck,
        settings,
      });

      return {
        isPrescriptionFailed,
        organizations: convertPmOrganizationsFromLink(organization),
        objects: convertObjectsFromLink(linkedKnm.pmErknm?.objects),
        events: convertEventsFromLink(linkedKnm.pmErknm?.events),
        requirements: convertPmRequirementsFromLink(linkedKnm.pmErknm?.requirements),
      };
    }
  }

  return undefined;
};

/**
 * Отбирает предписания связанного паспорта, по которым можно проводить повторную проверку.
 * Если предписания выбраны пользователем (рейдовый осмотр) — проверяем только их.
 */
const selectValidPrescriptions = <T extends TPrescriptionDecision>(
  decisions: T[] | null | undefined,
  isEnablePrescriptionCheck: boolean,
  selectedPrescriptionKeys?: string[],
): { error?: string; prescriptions: T[] } => {
  const prescriptions = (decisions ?? []).filter(isPrescription);

  if (!prescriptions.length) {
    return { error: LinkValidationGeneralMessageText, prescriptions };
  }

  const selectedPrescriptions = selectedPrescriptionKeys?.length
    ? prescriptions.filter(prescription => selectedPrescriptionKeys.includes(getPrescriptionKey(prescription)))
    : prescriptions;

  if (!selectedPrescriptions.length) {
    return { error: LinkValidationGeneralMessageText, prescriptions: selectedPrescriptions };
  }

  const hasValidPrescription = selectedPrescriptions.some(prescription =>
    isPrescriptionNotCompleted(prescription, isEnablePrescriptionCheck),
  );

  if (!hasValidPrescription) {
    return { error: LinkValidationPrescriptionMessageText, prescriptions: selectedPrescriptions };
  }

  return { prescriptions: selectedPrescriptions };
};

/**
 * Отбирает предписания КНМ (вместе с их КЛ), по которым можно проводить повторную проверку.
 * Если предписания выбраны пользователем (рейдовый осмотр) — проверяем только их.
 */
const selectErknmPrescriptions = (
  linkedKnm: IKnm,
  isEnablePrescriptionCheck: boolean,
  selectedPrescriptionKeys?: string[],
): { error?: string; prescriptions: ILinkedPrescription[] } => {
  const prescriptions = getErknmPrescriptions(linkedKnm);

  if (!prescriptions.length) {
    return { error: LinkValidationGeneralMessageText, prescriptions };
  }

  const selectedPrescriptions = selectedPrescriptionKeys?.length
    ? prescriptions.filter(({ decision }) => selectedPrescriptionKeys.includes(getPrescriptionKey(decision)))
    : prescriptions;

  if (!selectedPrescriptions.length) {
    return { error: LinkValidationGeneralMessageText, prescriptions: selectedPrescriptions };
  }

  const hasValidPrescription = selectedPrescriptions.some(({ decision }) =>
    isPrescriptionNotCompleted(decision, isEnablePrescriptionCheck),
  );

  if (!hasValidPrescription) {
    return { error: LinkValidationPrescriptionMessageText, prescriptions: selectedPrescriptions };
  }

  return { prescriptions: selectedPrescriptions };
};

/** Контролируемые лица выбранных предписаний, без повторов */
const getPrescriptionOrganizations = (prescriptions: ILinkedPrescription[]): IKnmSubject248[] => {
  const organizations: IKnmSubject248[] = [];

  prescriptions.forEach(({ organization }) => {
    if (!organizations.includes(organization)) {
      organizations.push(organization);
    }
  });

  return organizations;
};

/**
 * ОБК, связанные с контролируемыми лицами (object.organizationErvkId ↔ organization.ervkId).
 * Если связанных нет — берём объекты, добавленные вручную, то есть без привязки к организации ЕРВК.
 */
const getObjectsRelatedToOrganizations = (
  objects: IKnmObject248[],
  organizations: IKnmSubject248[],
): IKnmObject248[] => {
  const organizationErvkIds = organizations
    .map(organization => organization.ervkId)
    .filter((ervkId): ervkId is string => Boolean(ervkId));

  const relatedObjects = objects.filter(object =>
    object.organizationErvkId?.some(ervkId => organizationErvkIds.includes(ervkId)),
  );

  return relatedObjects.length ? relatedObjects : objects.filter(object => !object.organizationErvkId?.length);
};

/**
 * [4.1.31] Исходный паспорт создан до включения настройки: проверяем только КНО, вид контроля
 * и наличие предписания, разделы текущего паспорта не предзаполняем.
 */
const validateLegacyPrescriptionCheckLink = async ({
  linkedKnm,
  controllingOrganizationIds,
  kindControlCode,
}: Pick<
  IValidatePrescriptionCheckLinkParams,
  'linkedKnm' | 'controllingOrganizationIds' | 'kindControlCode'
>): Promise<TLinkValidationResult> => {
  const knoDictValue = await fetchDictValue<IKnoOrganizationDictionary>(
    linkedKnm.pmErknm ? linkedKnm.pmErknm.knoOrganization : linkedKnm.knmErknm?.knoOrganization,
  );

  if (knoDictValue && knoDictValue.value.idStr?.toString() !== controllingOrganizationIds) {
    return LinkValidationNoPrescriptionMessageText;
  }

  const sourceSupervisionType = linkedKnm.pmErknm ? linkedKnm.pmErknm.supervisionType : linkedKnm.knmErknm?.kindControl;
  const supervisionDictValue = await fetchDictValue<ISupervisionType>(sourceSupervisionType);
  const sourceCode = supervisionDictValue?.value.code;

  if (kindControlCode && sourceCode && sourceCode !== kindControlCode) {
    return LinkValidationNoPrescriptionMessageText;
  }

  if (!hasKnmPrescription(linkedKnm)) {
    return LinkValidationNoPrescriptionMessageText;
  }

  return LEGACY_NO_PREFILL;
};

const validatePrescriptionCheckLink = async ({
  linkedKnm,
  isEnablePrescriptionCheck,
  isPm,
  settings,
  erknmStartDateString,
  controllingOrganizationIds,
  kindControlCode,
  selectedPrescriptionKeys,
}: IValidatePrescriptionCheckLinkParams): Promise<TLinkValidationResult> => {
  if (isSourceCreatedByOldRules(linkedKnm.created, settings)) {
    return validateLegacyPrescriptionCheckLink({ linkedKnm, controllingOrganizationIds, kindControlCode });
  }

  const erknmStartDate = parseDate({
    date: erknmStartDateString || new Date(),
    parseFormat: DATE_FORMAT_LONG_FNS,
  });

  if (linkedKnm.pmErknm) {
    const { error, prescriptions } = selectValidPrescriptions(
      linkedKnm.pmErknm.subject?.pmResultDecisions,
      isEnablePrescriptionCheck,
      selectedPrescriptionKeys,
    );

    if (error) {
      return error;
    }

    const violations = getMatchedViolation(prescriptions, `${ROUTE_PM(linkedKnm.id)}`, erknmStartDate);

    if (linkedKnm.pmErknm?.subject && linkedKnm.pmErknm?.objects) {
      return {
        organizations: convertPmOrganizationsFromLink(linkedKnm.pmErknm?.subject),
        objects: convertObjectsFromLink(linkedKnm.pmErknm?.objects),
        events: isPm ? null : convertEventsFromLink(linkedKnm.pmErknm?.events),
        requirements: isPm ? null : convertErknmRequirementsFromLink(violations),
      };
    }

    return LinkValidationGeneralMessageText;
  }

  if (linkedKnm.knmErknm) {
    const { error, prescriptions } = selectErknmPrescriptions(
      linkedKnm,
      isEnablePrescriptionCheck,
      selectedPrescriptionKeys,
    );

    if (error) {
      return error;
    }

    const violations = getMatchedViolation(
      prescriptions.map(prescription => prescription.decision),
      `${ROUTE_KNM(linkedKnm.id)}`,
      erknmStartDate,
    );

    if (linkedKnm.knmErknm?.organizations && linkedKnm.knmErknm?.objects) {
      /**
       * Поле "Предписание" показывается только для рейдового осмотра — только там КЛ и ОБК
       * ограничиваются выбранными предписаниями. В остальных случаях копируется весь паспорт.
       */
      const isFilledBySelectedPrescriptions = Boolean(selectedPrescriptionKeys?.length);
      const organizations = isFilledBySelectedPrescriptions
        ? getPrescriptionOrganizations(prescriptions)
        : linkedKnm.knmErknm.organizations;
      const objects = isFilledBySelectedPrescriptions
        ? getObjectsRelatedToOrganizations(linkedKnm.knmErknm.objects, organizations)
        : linkedKnm.knmErknm.objects;

      return {
        organizations: convertErknmOrganizationsFromLink(organizations),
        objects: convertObjectsFromLink(objects),
        events: isPm ? null : convertEventsFromLink(linkedKnm.knmErknm?.events),
        requirements: isPm ? null : convertErknmRequirementsFromLink(violations),
      };
    }

    return LinkValidationGeneralMessageText;
  }

  return undefined;
};

/**
 * Проверяет выбранный по ссылке паспорт и возвращает либо текст ошибки,
 * либо данные, которыми нужно заполнить текущий паспорт.
 */
export const validateSelectedLinkKnm = async ({
  knm,
  cachedLinkKnm,
  settings,
  reasonTypeName,
  isPm,
  isErp,
  isEnablePaperlessDocument,
  erknmStartDateString,
  controllingOrganizationIds,
  kindControlCode,
  passportId,
  selectedPrescriptionKeys,
}: IValidateSelectedLinkKnmParams): Promise<TLinkValidationResult> => {
  const linkedKnm: IKnm = cachedLinkKnm && cachedLinkKnm.id === knm.id ? cachedLinkKnm : await fetchKnm(knm.id);

  const isEnablePrescriptionCheck = isLaterThanAdminSetting(
    linkedKnm.created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_105_166_25_4_1_30_STARTS_FROM,
  );

  if (reasonTypeName === KnmReasonTypeNameEnum.IMPOSSIBILITY_OF_PREVIOUS) {
    return validateImpossibilityOfPreviousLink({
      linkedKnm,
      isEnablePrescriptionCheck,
      settings,
      isEnablePaperlessDocument,
      isPm,
      erknmStartDateString,
      controllingOrganizationIds,
      passportId,
    });
  }

  if (isPassportByCheckPrescription(reasonTypeName) && isEnablePaperlessDocument && !isErp) {
    return validatePrescriptionCheckLink({
      linkedKnm,
      isEnablePrescriptionCheck,
      isPm,
      settings,
      erknmStartDateString,
      controllingOrganizationIds,
      kindControlCode,
      selectedPrescriptionKeys,
    });
  }

  return undefined;
};
